import { useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { Rollout, RolloutBoard } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { Button } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { waveLabelKey } from './rollout';

/**
 * Stopping a rollout, which is not an undo and does not pretend to be one.
 *
 * What has moved stays where it is. Putting a fleet back is another rollout,
 * aimed at the version it came from, and it goes in waves for the same reasons
 * the first one did — so the dialog says that rather than leaving somebody to
 * press this expecting their clusters to return.
 *
 * The wave that is moving stops at the next tenancy rather than mid-tenancy: a
 * move already under way finishes, and no further one begins.
 */
export function StopRolloutDialog({
  rollout,
  onClose,
  onStopped,
}: {
  rollout: Rollout;
  onClose: () => void;
  onStopped: (board: RolloutBoard) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function stop(): Promise<void> {
    setFailure(null);
    setBusy(true);
    try {
      onStopped(await api.provider.stopRollout());
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('rolloutStop.title')}
      subtitle={t('rolloutStop.subtitle', {
        target: rollout.target,
        wave: t(waveLabelKey(rollout.wave)),
      })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="danger" disabled={busy} onClick={() => void stop()}>
            {busy ? `${t('rolloutStop.submitting')}…` : t('rolloutStop.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <ErrorAlert error={failure} />}

        <Alert tone="warn" title={t('rolloutStop.effect.title')}>
          <p>{t('rolloutStop.effect.moved')}</p>
          <p style={{ marginTop: 8 }}>{t('rolloutStop.effect.back')}</p>
          <p style={{ marginTop: 8 }}>{t('rolloutStop.effect.next')}</p>
          <p style={{ marginTop: 8 }}>{t('rolloutStop.effect.ends')}</p>
        </Alert>
      </div>
    </Modal>
  );
}
